<script setup>
import { useAppSettingsStore } from '@convatec/180medical-website/app/stores/app-settings'

const { model } = defineProps(['model'])
const appSettingsStore = useAppSettingsStore()
const { showFooterForm } = useLayoutFlags()
showFooterForm.value = true
const contentId = model?.contentLink?.id

const { data } = await useFetch('/api/articles/getArticleMeta', {
  method: 'GET',
  key: `article-meta-${contentId}`,
  query: {
    contentId,
  },
  enabled: !!contentId,
})

const breadcrumbItems = computed(() => {
  return model?.breadcrumbLinks?.value?.map((link, index) => ({
    id: index,
    label: link.text || link.title,
    to: link.href,
  })) || []
})
</script>

<template>
  <div class="article-page">
    <ArticleHeroBlock
      color="navy"
      :title="model?.pageTitle?.value"
      :image="model?.aprimoArticleHeaderImage?.url"
      :image-width="model?.aprimoArticleHeaderImage?.width"
      :image-height="model?.aprimoArticleHeaderImage?.height"
      :image-alt-text="model?.headerImageAltText?.value"
    >
      <BreadCrumb :items="breadcrumbItems" />
    </ArticleHeroBlock>
    <div class="container-fluid">
      <div class="article-page__topics prose">
        <p>
          <strong>Topics:</strong>
          <em>
            <template v-if="data?.articleCategories">
              <NuxtLink
                v-for="(cat, i) in data.articleCategories"
                :key="cat.articleCategoryName"
                :href="cat.articleCategoryUrl"
                class="px-1 underline"
              >
                {{ cat.articleCategoryName + (i !== data.articleCategories.length - 1 ? "," : "") }}
              </NuxtLink>
            </template>
          </em>
        </p>
      </div>

      <div class="article-page__wrap">
        <div class="main-body">
          <div class="article-page__body">
            <div class="body-block-area w-full">
              <div
                class="prose mb-20 mx-auto"
                v-html="stripEmptyHeadings(model.pageBody.value)"
              />
              <GenericsContentArea
                v-if="model.bodyBlockArea?.expandedValue"
                :model="model.bodyBlockArea.expandedValue"
              />
            </div>
            <aside class="sidebar">
              <div class="sidebar__sticky">
                <ScrollToFormBlock
                  v-if="appSettingsStore.contactSettings.scrollToTitle"
                  :title="appSettingsStore?.contactSettings?.scrollToTitle"
                  :body="appSettingsStore?.contactSettings?.scrollToBody"
                  :text="appSettingsStore?.contactSettings?.scrollToText"
                />
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>

    <div class="page-block-area flex flex-col items-center w-full">
      <GenericsContentArea
        v-if="model.pageBlockArea?.expandedValue"
        :model="model.pageBlockArea.expandedValue"
      />
    </div>

    <AuthorBlock :model="model.articleAuthor.expandedValue" />

    <div class="page-block-area flex flex-col items-center w-full">
      <ArticleListingBlock
        v-if="data?.relatedArticles"
        title="Related Articles"
        summary=""
        :show-article-summaries="model.showArticleSummaries?.value"
        :show-article-topics="model.showArticleTopics?.value"
        :cards="data.relatedArticles"
        :category-url="data.articleCategories && data.articleCategories.length ? data.articleCategories[0].articleCategoryUrl : ''"
      />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/assets/css/pages/article-page.scss';
</style>
